import {
  Paper,
  Stack,
  Switch,
  FormControlLabel,
  FormGroup,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import { useCallback, useState, useEffect } from "react";
import { CancelButton, MainButton } from "../components/buttons";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FormGrid, GridItem1 } from "../components/grids";
import { useAppDispatch, useAppSelector } from "../store";
import { setView, Views, setLastError } from "../store/session";
import { useMutation } from "@apollo/client";
import {
  CREATE_DETOX_SESSION,
  UPDATE_DETOX_SESSION,
  CREATE_DETOX_EVENT,
} from "../Queries/DetoxQueries";
import { formatDateForInput, mapGraphQLError, computeStatus, getTodayString, parseDateFromInput } from "../utils/detox";
import { isBefore, isValid } from "date-fns";
import { DetoxEventManager } from "../components/DetoxEventManager";
import dayjs from "dayjs";

// NOTE: Use CommonJS entrypoint to avoid webpack "fully specified" ESM resolution issues under CRA.
const { DatePicker } = require("@mui/x-date-pickers/DatePicker") as typeof import("@mui/x-date-pickers/DatePicker");

export const EditDetoxSession = () => {
  const user = useAppSelector((state) => state.session.user);
  const session = useAppSelector((state) => state.session.editDetoxSession);
  const dispatch = useAppDispatch();
  
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isCancelled, setIsCancelled] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>("");
  const [temporaryEvents, setTemporaryEvents] = useState<Array<{ name: string; description?: string | null; link?: string | null }>>([]);

  useEffect(() => {
    if (session) {
      setStartDate(formatDateForInput(session.start_date));
      setEndDate(formatDateForInput(session.end_date));
      setIsCancelled(session.status === 'cancelled');
      setTemporaryEvents([]); // Clear temporary events when editing existing session
    } else {
      // New session - set default dates
      const today = new Date();
      setStartDate(formatDateForInput(today));
      setEndDate(formatDateForInput(today));
      setIsCancelled(false);
      setTemporaryEvents([]); // Clear temporary events
    }
  }, [session]);

  const [createEvent] = useMutation(CREATE_DETOX_EVENT, {
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const [createSession, { loading: creating }] = useMutation(CREATE_DETOX_SESSION, {
    onCompleted: async (data) => {
      const newSessionId = data?.insert_digital_detox_sessions_one?.id;
      if (newSessionId && temporaryEvents.length > 0) {
        // Create all temporary events
        try {
          for (const event of temporaryEvents) {
            await createEvent({
              variables: {
                session_id: newSessionId,
                name: event.name,
                description: event.description || null,
                link: event.link || null,
              },
            });
          }
        } catch (error) {
          // Error already handled in createEvent mutation
          return; // Don't navigate away if event creation fails
        }
      }
      dispatch(setView(Views.DigitalDetox));
    },
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const [updateSession, { loading: updating }] = useMutation(UPDATE_DETOX_SESSION, {
    onCompleted: () => {
      dispatch(setView(Views.DigitalDetox));
    },
    onError: (error) => {
      const friendlyError = mapGraphQLError(error);
      dispatch(setLastError(friendlyError));
    },
  });

  const handleCancel = useCallback(() => {
    dispatch(setView(Views.DigitalDetox));
  }, [dispatch]);

  const validateDates = useCallback((): boolean => {
    if (!startDate || !endDate) {
      setValidationError("Start date and end date are required");
      return false;
    }

    const start = parseDateFromInput(startDate);
    const end = parseDateFromInput(endDate);

    if (!start || !end || !isValid(start) || !isValid(end)) {
      setValidationError("Invalid date format");
      return false;
    }

    if (isBefore(end, start)) {
      setValidationError("End date must be the same as or after start date");
      return false;
    }

    setValidationError("");
    return true;
  }, [startDate, endDate]);

  const handleSave = useCallback(async () => {
    if (!validateDates()) {
      return;
    }

    if (!user) return;

    if (session?.id) {
      // Update existing session - build dynamic _set object
      const setObject: any = {
        start_date: startDate,
        end_date: endDate,
      };

      // Set status based on toggle: cancelled if on, compute from dates if off
      if (isCancelled) {
        setObject.status = 'cancelled';
      } 
      else {
        const computedStatus = computeStatus(startDate, endDate);
        setObject.status = computedStatus;
      }

      await updateSession({
        variables: {
          pk_columns: { id: session.id },
          set: setObject,
        },
      });
    } else {
      // Create new session - don't set status, let it be computed from dates
      // Events will be created in the onCompleted callback
      await createSession({
        variables: {
          start_date: startDate,
          end_date: endDate,
        },
      });
    }
  }, [session, startDate, endDate, isCancelled, user, validateDates, createSession, updateSession, temporaryEvents, createEvent]);

  const editVerb = session?.id ? "Edit" : "Create";
  const isLoading = creating || updating;
  
  // Get today's date in YYYY-MM-DD format for min date restriction
  const today = getTodayString();

  return (
    <Stack spacing={1}>
      <Paper>
        <Stack spacing={1} padding={2}>
          <Stack direction={"row"} justifyContent="space-between">
            <Typography variant="h4">{editVerb} Detox Session</Typography>
          </Stack>
          
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            disabled={isLoading}
            sx={{ alignSelf: 'flex-start' }}
          >
            Back
          </Button>
          
          <br />
          
          {validationError && (
            <Typography color="error" variant="body2">
              {validationError}
            </Typography>
          )}

          <FormGrid>
            <GridItem1>
              <DatePicker
                label="Start Date"
                value={startDate ? dayjs(startDate) : null}
                disablePast
                onChange={(newValue) => {
                  const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                  // Prevent selecting new dates before today
                  if (newDate && newDate < today) {
                    setValidationError("Start date cannot be before today");
                    return;
                  }
                  setStartDate(newDate);
                  setValidationError("");
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    id: "start-date",
                    required: true,
                    error: !!validationError,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </GridItem1>
            <GridItem1>
              <DatePicker
                label="End Date"
                value={endDate ? dayjs(endDate) : null}
                disablePast
                onChange={(newValue) => {
                  const newDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                  // Prevent selecting new dates before today
                  if (newDate && newDate < today) {
                    setValidationError("End date cannot be before today");
                    return;
                  }
                  setEndDate(newDate);
                  setValidationError("");
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    id: "end-date",
                    required: true,
                    error: !!validationError,
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </GridItem1>
            {/* Only show cancelled toggle when editing an existing session */}
            {session?.id && (
              <GridItem1>
                <FormGroup>
                  <FormControlLabel
                    labelPlacement="top"
                    control={
                      <Switch
                        checked={isCancelled}
                        onChange={(event) => setIsCancelled(event.target.checked)}
                      />
                    }
                    label="Cancelled"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Mark this session as cancelled
                  </Typography>
                </FormGroup>
              </GridItem1>
            )}
          </FormGrid>

          {/* Show events section only for new sessions */}
          {!session?.id && (
            <>
              <Divider />
              <Stack spacing={2}>
                <DetoxEventManager
                  events={temporaryEvents}
                  onEventsChange={setTemporaryEvents}
                  isTemporary={true}
                />
              </Stack>
            </>
          )}
          
          <Stack spacing={1} direction="row-reverse">
            <MainButton
              variant="contained"
              onClick={handleSave}
              disabled={isLoading || !!validationError}
            >
              {session?.id ? "Save" : "Create"}
            </MainButton>
            <CancelButton variant="outlined" onClick={handleCancel} disabled={isLoading}>
              Cancel
            </CancelButton>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

